/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IAICustomizationWorkspaceService } from '../../common/aiCustomizationWorkspaceService.js';
import { IChatWidgetService } from '../chat.js';
import { IChatService } from '../../common/chatService/chatService.js';
import { ChatModeKind } from '../../common/constants.js';
import { PromptsType } from '../../common/promptSyntax/promptTypes.js';
import { getPromptFileDefaultLocations } from '../../common/promptSyntax/config/promptFileLocations.js';
import { IPromptsService, PromptsStorage } from '../../common/promptSyntax/service/promptsService.js';
import { URI } from '../../../../../base/common/uri.js';
import { isEqualOrParent } from '../../../../../base/common/resources.js';
import { ICommandService } from '../../../../../platform/commands/common/commands.js';
import { IQuickInputService } from '../../../../../platform/quickinput/common/quickInput.js';
import { localize } from '../../../../../nls.js';

/**
 * Service that opens an AI-guided chat session to help the user create
 * a new customization (agent, skill, instructions, prompt, hook).
 *
 * Opens a new chat in agent mode, then sends a request with hidden
 * system instructions (modeInstructions) that guide the AI through
 * the creation process. The user sees only their message.
 */
export class CustomizationCreatorService {

	constructor(
		@ICommandService private readonly commandService: ICommandService,
		@IChatService private readonly chatService: IChatService,
		@IChatWidgetService private readonly chatWidgetService: IChatWidgetService,
		@IAICustomizationWorkspaceService private readonly workspaceService: IAICustomizationWorkspaceService,
		@IPromptsService private readonly promptsService: IPromptsService,
		@IQuickInputService private readonly quickInputService: IQuickInputService,
	) { }

	async createWithAI(type: PromptsType): Promise<void> {
		// Ask for the name before entering chat
		const typeLabel = getTypeLabel(type);
		const name = await this.quickInputService.input({
			prompt: localize('generateName', "Name for the new {0}", typeLabel),
			placeHolder: localize('generateNamePlaceholder', "e.g., my-{0}", typeLabel),
			validateInput: async (value) => {
				if (!value || !value.trim()) {
					return localize('nameRequired', "Name is required");
				}
				return undefined;
			}
		});
		if (!name) {
			return;
		}
		const trimmedName = name.trim();

		// TODO: The 'Generate X' flow currently opens a new chat that is not connected
		// to the active workspace. For this to fully work, the background agent needs to
		// accept a workspace parameter so the new session can write files into the correct
		// directory and have those changes tracked.

		// Capture project root BEFORE opening new chat (which may change active session)
		const targetDir = await this.resolveTargetDirectoryWithPicker(type);
		if (targetDir === null) {
			return; // User cancelled the picker
		}
		const systemInstructions = buildAgentInstructions(type, targetDir, trimmedName);
		const userMessage = buildUserMessage(type, targetDir, trimmedName);

		// Start a new chat, then send the request with hidden instructions
		await this.commandService.executeCommand('workbench.action.chat.newChat');

		// Grab the now-active widget's session and send with hidden instructions
		const widget = this.chatWidgetService.lastFocusedWidget;
		const sessionResource = widget?.viewModel?.sessionResource;
		if (!sessionResource) {
			return;
		}

		await this.chatService.sendRequest(sessionResource, userMessage, {
			modeInfo: {
				kind: ChatModeKind.Agent,
				isBuiltin: false,
				modeId: 'custom',
				applyCodeBlockSuggestionId: undefined,
				modeInstructions: {
					name: 'customization-creator',
					content: systemInstructions,
					toolReferences: [],
				},
			},
		});
	}

	/**
	 * Resolves the workspace directory for a new customization file based on the
	 * active project root.
	 */
	resolveTargetDirectory(type: PromptsType): URI | undefined {
		return resolveWorkspaceTargetDirectory(this.workspaceService, type);
	}

	/**
	 * Resolves the workspace directory for a new customization file.
	 * If multiple local source folders exist, shows a picker to let the user choose.
	 *
	 * @returns the resolved URI, `undefined` when no folder is available,
	 *          or `null` when the user cancelled the picker.
	 */
	private async resolveTargetDirectoryWithPicker(type: PromptsType): Promise<URI | undefined | null> {
		const allFolders = await this.promptsService.getSourceFolders(type);
		const projectRoot = this.workspaceService.getActiveProjectRoot();

		// Filter to only workspace-scoped folders (under the active project root).
		// Don't rely on storage tags — tilde-expanded user paths can be tagged local.
		// Deduplicate by URI to avoid inflated counts and duplicate picker entries.
		const seen = new Set<string>();
		const workspaceFolders = projectRoot
			? allFolders.filter(f => {
				if (!isEqualOrParent(f.uri, projectRoot)) {
					return false;
				}
				const key = f.uri.toString();
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			})
			: [];

		if (workspaceFolders.length === 0) {
			// No workspace folders — fall back to the existing resolution logic
			return this.resolveTargetDirectory(type);
		}

		if (workspaceFolders.length === 1) {
			return workspaceFolders[0].uri;
		}

		// Multiple directories — ask the user which one to use
		const items = workspaceFolders.map(folder => ({
			label: this.promptsService.getPromptLocationLabel(folder),
			description: folder.uri.fsPath,
			uri: folder.uri,
		}));

		const picked = await this.quickInputService.pick(items, {
			placeHolder: localize('selectTargetDirectory', "Select a directory for the new customization file"),
		});

		return picked?.uri ?? null;
	}

	/**
	 * Resolves the user-level directory for a new customization file.
	 */
	async resolveUserDirectory(type: PromptsType): Promise<URI | undefined> {
		return resolveUserTargetDirectory(this.promptsService, type);
	}
}

/**
 * Resolves the workspace directory for a new customization file based on the active project root.
 */
export function resolveWorkspaceTargetDirectory(workspaceService: IAICustomizationWorkspaceService, type: PromptsType): URI | undefined {
	const basePath = workspaceService.getActiveProjectRoot();
	if (!basePath) {
		return undefined;
	}
	const defaultLocations = getPromptFileDefaultLocations(type);
	const localLocation = defaultLocations.find(loc => loc.storage === PromptsStorage.local);
	if (!localLocation) {
		return basePath;
	}
	return URI.joinPath(basePath, localLocation.path);
}

/**
 * Resolves the user-level directory for a new customization file.
 * Delegates to IPromptsService.getSourceFolders() which returns the appropriate
 * user root (VS Code profile in core, ~/.copilot in sessions).
 */
export async function resolveUserTargetDirectory(
	promptsService: IPromptsService,
	type: PromptsType,
): Promise<URI | undefined> {
	const folders = await promptsService.getSourceFolders(type);
	const userFolder = folders.find(f => f.storage === PromptsStorage.user);
	return userFolder?.uri;
}

//#region Agent Instructions

/**
 * Builds the hidden system instructions for the customization creator agent.
 * Sent as modeInstructions - invisible to the user.
 */
function buildAgentInstructions(type: PromptsType, targetDir: URI | undefined, name: string): string {
	const targetHint = targetDir
		? `\nIMPORTANT: Save the file to this directory: ${targetDir.fsPath}. The name is "${name}".`
		: `\nThe name is "${name}".`;

	const writePolicy = `

CRITICAL WORKFLOW:
- In your VERY FIRST response, you MUST immediately create the file on disk from a starter template with placeholder content. Do not ask questions first -- write the file first so it appears in the diff view, then ask the user how they want to customize it.
- Every subsequent message from the user should result in you updating that same file on disk with the requested changes.
- Always write the complete file content, not partial diffs.${targetHint}`;

	switch (type) {
		case PromptsType.agent:
			return `You are a helpful assistant that guides users through creating a new custom AI agent.${writePolicy}

Create a file named "${name}.agent.md" with YAML frontmatter (name, description, tools) and system instructions. Ask the user what it should do.`;

		case PromptsType.skill:
			return `You are a helpful assistant that guides users through creating a new skill.${writePolicy}

Create a directory named "${name}" with a SKILL.md file inside it. The file should have YAML frontmatter (name, description) and instructions. Ask the user what it does.`;

		case PromptsType.instructions:
			return `You are a helpful assistant that guides users through creating a new instructions file.${writePolicy}

Create a file named "${name}.instructions.md" with YAML frontmatter (description, optional applyTo) and actionable content. Ask the user what it should cover.`;

		case PromptsType.prompt:
			return `You are a helpful assistant that guides users through creating a new reusable prompt.${writePolicy}

Create a file named "${name}.prompt.md" with YAML frontmatter (name, description) and prompt content. Ask the user what it should do.`;

		case PromptsType.hook:
			return `You are a helpful assistant that guides users through creating a new hook.${writePolicy}

Ask the user when the hook should trigger and what it should do, then write the configuration file.`;

		default:
			return `You are a helpful assistant that guides users through creating a new AI customization file.${writePolicy}

Ask the user what they want to create, then guide them step by step.`;
	}
}

//#endregion

//#region User Messages

/**
 * Builds the user-visible message that opens the chat.
 * Includes the target path so the agent knows where to write the file.
 */
function buildUserMessage(type: PromptsType, targetDir: URI | undefined, name: string): string {
	const pathHint = targetDir ? ` Write it to \`${targetDir.fsPath}\`.` : '';

	switch (type) {
		case PromptsType.agent:
			return `Help me create a new custom agent called "${name}".${pathHint}`;
		case PromptsType.skill:
			return `Help me create a new skill called "${name}".${pathHint}`;
		case PromptsType.instructions:
			return `Help me create new instructions called "${name}".${pathHint}`;
		case PromptsType.prompt:
			return `Help me create a new prompt called "${name}".${pathHint}`;
		case PromptsType.hook:
			return `Help me create a new hook called "${name}".${pathHint}`;
		default:
			return `Help me create a new customization called "${name}".${pathHint}`;
	}
}

function getTypeLabel(type: PromptsType): string {
	switch (type) {
		case PromptsType.agent: return 'agent';
		case PromptsType.skill: return 'skill';
		case PromptsType.instructions: return 'instructions';
		case PromptsType.prompt: return 'prompt';
		case PromptsType.hook: return 'hook';
		default: return 'customization';
	}
}

//#endregion
